import type { AccessLevel } from "./permission"

// AI Capability types — aligned with backend CapabilityType enum
// (vendor/joint-common/backend/app/modules/jerry/enums.py: CapabilityType AI 子集)
export type CapabilityType = "skill" | "agent" | "playbook" | "mcp_tool"
export type AssetDomain = "ai_capability" | "business_knowledge"
export type CapabilityScope = "tenant" | "personal" | "matter_type"
// US-G03 自定义顾问额外引入 "team"（团队范围）——不并入上面的联合类型，因为
// CapabilityScope 在业务知识域（KnowledgeDetailDrawer/KnowledgeItemCard）被当
// 作穷尽 Record 的 key 类型消费，扩它会牵连无关模块。能力卡的 `scope` 字段在
// 运行时可能是 "team"（渲染层 SCOPE_LABELS/SCOPE_BADGE_CLASS 是 Record<string,
// ...> 宽松类型，能正确处理），仅类型声明保守。
// US-G02 一等绑定：引擎侧目标类型（由卡片 type 派生：agent 类卡 → "agent"，
// skill 类卡 → "skill"）；binding_status 是最近一次引擎存在性核验的结果。
export type CapabilityEngineRefType = "agent" | "skill"
export type CapabilityBindingStatus = "verified" | "unverified"

export interface Capability {
  id: string
  title: string
  description: string | null
  type: CapabilityType
  assetDomain: AssetDomain
  content: string | null          // Markdown prompt body (only for inspect/admin)
  practiceAreas: string[] | null  // JSONB array
  tags: string[] | null
  scope: CapabilityScope
  scopeRef: string | null         // scope reference id (e.g. matter_type code)
  createdBy: string | null
  isActive: boolean
  version: number
  tenantId: string
  createdAt: string
  updatedAt: string

  // Permission info returned by API
  permissionLevel: AccessLevel | null

  // Computed (joined from user table)
  createdByName: string | null

  // Starred by current user
  isStarred?: boolean

  // US-G02 一等绑定：引擎侧目标（agent 名 / skill id）。null/缺省 = 存量手工卡，
  // 无绑定，保持预填文本行为（点"调用此能力"不新建顾问指定）。
  engineRefType?: CapabilityEngineRefType | null
  engineRefName?: string | null
  bindingStatus?: CapabilityBindingStatus | null

  // 批 5（能力隐式调用架构 §7.3）：能力详情面板的引擎语义字段回落。仅对已绑定
  // skill 类卡出现（GET /capabilities/{id} 单项详情才取，列表接口不含这些
  // 字段，避免逐行探测引擎）。`engineUnavailable` 为 true 时其余 engine* 字段
  // 缺省，界面回落本地 title/description 并标注「引擎信息暂不可用」。
  engineUnavailable?: boolean
  engineWhenToUse?: string | null
  engineNotFor?: { condition: string; use_instead: string }[]
  enginePreconditions?: { id: string; description: string }[]
  engineCostClass?: "trivial" | "light" | "heavy" | string | null
  engineTypicalDuration?: string | null
  /** 评审 P3：产出类型（skill.yaml `produces` 字段），如 `risk_report`。 */
  engineProduces?: string | null

  /** V8（契约已定）：引擎可达但注册表中已无 engineRefName 指名的目标（引擎侧
   *  下线/改名）。区别于 `engineUnavailable`（引擎整体不可达）：目标缺失是
   *  确定的判定，提示律师改绑或解绑，而不是「暂不可用」的中性降级。 */
  engineTargetMissing?: boolean

  // 任务二阶段一（读）：agent 类已绑定卡的引擎工具授权——agent.yaml `tools`
  // 条目（含 `mcp.<server>.*` 通配条目）。仅对 agent 类已绑定卡出现
  // （GET /capabilities/{id} 单项详情才取）；引擎不可达时缺省并置
  // `engineUnavailable`（上面批 5 字段组的同一降级约定）。
  // V11（契约已定）：后端把条目从裸 string 升级为 {name, label}——name 为
  // agent.yaml 原始条目，label 为后端解析的律师可读名（解析不出回落 name）。
  engineTools?: EngineToolInfo[] | null
}

/** agent.yaml `tools` 单条授权条目（V11：后端解析律师可读名）。 */
export interface EngineToolInfo {
  /** 原始条目（如 `mcp.qcc-*`、`web-search`）。 */
  name: string
  /** 后端解析的展示名；解析不出时回落 name。 */
  label: string
}

// List item — returned without content field for invoke-level users
export type CapabilityListItem = Omit<Capability, "content"> & {
  useCount?: number;
}

export interface CapabilityCreateRequest {
  title: string
  description: string
  type: CapabilityType
  practiceAreas?: string[]
  tags?: string[]
  scope: CapabilityScope
  scopeRef?: string
  content?: string  // Markdown body
  // 任务二阶段二（写）：绑定引擎目标（agent 名 / skill id）。后端按卡片 type
  // 派生 engine_ref_type 并向引擎核验目标存在；仅 skill/agent 类型可绑定。
  // 省略/空 = 存量手工卡（预填文本调用）。
  engineRefName?: string | null
}

export interface CapabilityUpdateRequest {
  title?: string
  description?: string
  practiceAreas?: string[]
  tags?: string[]
  scope?: CapabilityScope
  scopeRef?: string
  content?: string
  isActive?: boolean
  // 显式 null = 解绑（回落预填文本）；字符串 = 改绑（后端重新核验引擎目标）；
  // 字段省略 = 不动现有绑定。仅在用户实际改动时随 payload 提交。
  engineRefName?: string | null
}

// ---------------------------------------------------------------------------
// 任务二阶段二：GET /engine/catalog —— 可绑定引擎目标目录（创建/编辑弹窗
// 「绑定引擎目标」选择器数据源）。目录为平台级 builtin 注册表（引擎侧已过滤
// cst_ 前缀 addon 与未激活项）；选择器取值直接写入 engineRefName。
// ---------------------------------------------------------------------------

/** 可绑定的 builtin agent（agent 类卡按 name 绑定）。 */
export interface EngineCatalogAgent {
  name: string
  displayName: string
}

/** 可绑定的激活 skill（skill 类卡按 id 绑定）。 */
export interface EngineCatalogSkill {
  id: string
  name: string
}

export interface EngineCatalog {
  agents: EngineCatalogAgent[]
  skills: EngineCatalogSkill[]
}

// Practice area options — 权威编码溯源 docs/glossary.md「AI 能力执业领域编码」。
// capital_markets（2026-08-24 新增，境内资本市场与发行上市业务，见该节登记
// 文字）登记顺序为 data_privacy 之后、cross_border_us 之前，此处保持一致。
export const PRACTICE_AREAS = [
  "contract",
  "merger_acquisition",
  "litigation",
  "employment",
  "intellectual_property",
  "compliance",
  "data_privacy",
  "capital_markets",
  "cross_border_us",
  "cross_border_hk_ipo",
] as const

export type PracticeArea = (typeof PRACTICE_AREAS)[number]
